// Shared by the Next.js application and the standalone Wonde worker.
const meanings = {
  PRESENT: ['Present', 'Present', 'IN'],
  AUTHORISED: ['Authorised Absence', 'Authorised', 'OUT'],
  UNAUTHORISED: ['Unauthorised Absence', 'Unauthorised', 'OUT'],
  VOID: ['Attendance not required', 'Not Required', 'OUT'],
};

// Academic years run from September through August, matching the standard codes.
function getAttendanceAcademicYear(date) {
  const value = new Date(date);
  return value.getMonth() < 8 ? value.getFullYear() - 1 : value.getFullYear();
}

function getAttendanceDateKey(value) {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Prisma @db.Date values are UTC dates; session dates follow the local calendar.
function getValidityDateKey(value) {
  if (!value) return null;
  return typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10);
}

const validitySchedules = new WeakMap();
function getAttendanceCodeDateKey(codes, date) {
  if (!validitySchedules.has(codes)) {
    const years = new Map();
    for (const code of codes) {
      if (!years.has(code.year)) years.set(code.year, new Set());
      for (const [value, increment] of [[code.startDate, 0], [code.endDate, 1]]) {
        const key = getValidityDateKey(value);
        if (!key) continue;
        const [y, m, d] = key.split('-').map(Number);
        const boundary = new Date(y, m - 1, d + increment);
        if (getAttendanceAcademicYear(boundary) === code.year) years.get(code.year).add(getAttendanceDateKey(boundary));
      }
    }
    validitySchedules.set(codes, new Map([...years].map(([year, boundaries]) => [year, [...boundaries].sort()])));
  }
  const year = getAttendanceAcademicYear(date);
  const day = getAttendanceDateKey(date);
  const boundaries = validitySchedules.get(codes).get(year) || [];
  const boundary = boundaries.filter((value) => value <= day).pop() || `${year}-09-01`;
  return `${year}:${boundary}`;
}

function getCustomAttendanceCodesForDate(codes, date) {
  const year = getAttendanceAcademicYear(date);
  const day = getAttendanceDateKey(date);
  return codes.filter((code) => {
    const start = getValidityDateKey(code.startDate);
    const end = getValidityDateKey(code.endDate);
    if (code.year !== year || (start && day < start) || (end && day > end)) return false;
    // Inactive codes with a dated end remain valid for historical attendance.
    if (code.active === false && !end) return false;
    return true;
  }).sort((a, b) => {
    const startOrder = (getValidityDateKey(a.startDate) || '').localeCompare(getValidityDateKey(b.startDate) || '');
    if (startOrder) return startOrder;
    // With equal starts, a bounded historical version is more specific.
    return (getValidityDateKey(b.endDate) || '9999-12-31').localeCompare(getValidityDateKey(a.endDate) || '9999-12-31');
  });
}

function getAttendanceCodeChangeDates(codes, dates) {
  const start = getAttendanceSessionDate(dates.attStart, 0);
  const end = getAttendanceSessionDate(dates.attEnd, 0);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start > end) return [];
  const points = new Map([[getAttendanceDateKey(start), start]]);
  const add = (date) => {
    if (date >= start && date <= end) points.set(getAttendanceDateKey(date), date);
  };
  for (let year = getAttendanceAcademicYear(start); year <= getAttendanceAcademicYear(end); year++) add(new Date(year, 8, 1));
  for (const code of codes) {
    for (const [value, increment] of [[code.startDate, 0], [code.endDate, 1]]) {
      const key = getValidityDateKey(value);
      if (!key) continue;
      const [y, m, d] = key.split('-').map(Number);
      add(new Date(y, m - 1, d + increment));
    }
  }
  return [...points.values()].sort((a, b) => a - b);
}

function getAttendanceSessionDate(startDate, index) {
  const date = new Date(startDate);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + Math.floor(index / 2));
  return date;
}

function parseAttendanceMarks(marks, customCodes, startDate, standardMultiCodes) {
  if (Array.isArray(marks)) return marks;
  const tokensByPeriod = new Map();
  const result = [];
  for (let i = 0; i < (marks || '').length;) {
    const date = startDate ? getAttendanceSessionDate(startDate, result.length) : null;
    const period = date ? getAttendanceCodeDateKey(customCodes, date) : null;
    if (!tokensByPeriod.has(period)) {
      const active = date ? getCustomAttendanceCodesForDate(customCodes, date) : customCodes;
      tokensByPeriod.set(period, [...new Set([
        ...standardMultiCodes,
        ...active.map((code) => code.code || code.Mark),
      ])].filter((code) => typeof code === 'string' && code.length > 1)
        .sort((a, b) => b.length - a.length));
    }
    const mark = tokensByPeriod.get(period).find((code) => marks.startsWith(code, i)) || marks[i];
    result.push(mark);
    i += mark.length;
  }
  return result;
}

// Calendar-day arithmetic keeps AM/PM positions stable across daylight saving.
function getAttendanceSessions(records, dates, customCodes, parseMarks) {
  const dayNumber = (value) => {
    const date = new Date(value);
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;
  };
  const sessions = [];
  const sorted = [...(records || [])].sort((a, b) => new Date(a.marksStartDate) - new Date(b.marksStartDate));
  for (const record of sorted) {
    const marks = parseMarks(record.marks, customCodes, record.marksStartDate);
    const start = dayNumber(record.marksStartDate);
    const first = Math.max(0, 2 * (dayNumber(dates.attStart) - start));
    const last = Math.min(marks.length, 2 * (dayNumber(dates.attEnd) - start + 1));
    for (let i = first; i < last; i++) {
      const date = getAttendanceSessionDate(record.marksStartDate, i);
      sessions.push({ mark: marks[i], date, session: i % 2 === 0 ? 'AM' : 'PM', year: getAttendanceAcademicYear(date) });
    }
  }
  return sessions;
}

function mergeCustomAttendanceCodes(standardCodes, customCodes) {
  if (!customCodes?.length) return standardCodes;
  const codes = new Map(standardCodes.map((code) => [code.Mark, code]));
  for (const code of customCodes) {
    let meaning = Object.hasOwn(meanings, code.type) && meanings[code.type];
    if (code.type === 'PRESENT' && code.shortMeaningDescription?.toLowerCase() === 'approved') {
      meaning = ['Approved Edu. Activity', 'Approved', code.physicalMeaning || 'OUT'];
    }
    if (code.type === 'VOID' && code.shortMeaningDescription?.toLowerCase() === 'no mark') {
      meaning = ['No mark', 'No Mark', 'NOMARK'];
    }
    if (!meaning || !code.code) continue;
    codes.set(code.code, {
      ...code,
      Id: code.externalId || code.wondeId || code.id,
      Mark: code.code,
      Description: code.description,
      MeaningDescription: meaning[0],
      ShortMeaningDescription: meaning[1],
      PhysicalMeaning: code.physicalMeaning || meaning[2],
    });
  }
  // These are generated by our sync for missing sessions and weekends.
  for (const mark of ['-', '#']) {
    codes.set(mark, standardCodes.find((code) => code.Mark === mark));
  }
  return [...codes.values()].filter(Boolean);
}

function getCustomAttendanceCodes(pupilOrSchool) {
  const school = pupilOrSchool?.school || pupilOrSchool;
  return school?.attendanceCodes || [];
}

function validateCustomAttendanceCodes(codes) {
  if (!Array.isArray(codes) || !codes.length) {
    throw new Error('Wonde returned no custom attendance codes');
  }
  for (const code of codes) {
    if (
      typeof code.id !== 'string' || !code.id.trim() ||
      typeof code.code !== 'string' || !code.code ||
      !Object.hasOwn(meanings, code.type)
    ) {
      throw new Error('Wonde returned an invalid custom attendance code');
    }
  }
  return codes;
}

module.exports = {
  getAttendanceDateKey,
  getValidityDateKey,
  getAttendanceCodeDateKey,
  getAttendanceCodeChangeDates,
  getAttendanceSessionDate,
  parseAttendanceMarks,
  getAttendanceSessions,
  getAttendanceAcademicYear,
  getCustomAttendanceCodesForDate,
  mergeCustomAttendanceCodes,
  getCustomAttendanceCodes,
  validateCustomAttendanceCodes,
};
